import { useState, useMemo, useEffect } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Avatar, Text } from '@/components/design-system'
import { Check, Plus, Search } from 'lucide-react'
import type { SharedProps, OrganizationSummary } from '@/frontend/types/inertia'

interface OrganizationSwitcherProps {
  onClose: () => void
  isOpen?: boolean
}

export default function OrganizationSwitcher({ onClose, isOpen }: OrganizationSwitcherProps) {
  const { organizations = [], auth } = usePage<SharedProps>().props
  const [searchQuery, setSearchQuery] = useState('')

  // Reset search when panel reopens
  useEffect(() => {
    if (isOpen) setSearchQuery('')
  }, [isOpen])
  const [isSwitching, setIsSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentOrgId = auth.user?.active_organization?.id

  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) return organizations
    const query = searchQuery.toLowerCase()
    return organizations.filter(org =>
      org.name.toLowerCase().includes(query)
    )
  }, [organizations, searchQuery])

  const handleSwitch = (org: OrganizationSummary) => {
    if (org.id === currentOrgId || isSwitching) return

    setIsSwitching(true)
    setError(null)
    router.post(`/dashboard/organizations/${org.slug}/switch`, {}, {
      onSuccess: () => {
        onClose()
      },
      onError: () => {
        setError('Erro ao trocar de organização. Tente novamente.')
      },
      onFinish: () => {
        setIsSwitching(false)
      },
      preserveState: false,
      preserveScroll: true
    })
  }

  return (
    <div className="py-2">
      {/* Search Input */}
      {organizations.length > 3 && (
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar organização..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            />
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="px-3 pb-2">
          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-500/10 dark:text-red-400 px-3 py-2 rounded-md">
            {error}
          </div>
        </div>
      )}

      {/* Organizations List */}
      <div>
        {filteredOrganizations.length > 0 ? (
          filteredOrganizations.map((org) => (
            <button
              key={org.id}
              data-testid={`organization-switcher-option-${org.id}`}
              onClick={() => handleSwitch(org)}
              disabled={isSwitching}
              className={`w-full flex items-center justify-between px-4 py-2 text-left transition-colors ${
                org.id === currentOrgId
                  ? 'bg-accent text-foreground'
                  : 'text-foreground hover:bg-accent'
              } ${isSwitching ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-3">
                <Avatar
                  name={org.name}
                  size="xs"
                  className="rounded"
                />
                <span className="truncate">{org.name}</span>
              </div>
              {org.id === currentOrgId && (
                <Check className="h-4 w-4 text-foreground flex-shrink-0" strokeWidth={1.75} />
              )}
            </button>
          ))
        ) : (
          <div className="px-4 py-2">
            <Text size="body-small" color="subdued">Nenhuma organização com esse nome</Text>
          </div>
        )}
      </div>

      {/* Create New Organization */}
      <div className="border-t border-border mt-2 pt-2">
        <Link
          href="/dashboard/organizations/new"
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 text-foreground hover:bg-accent transition-colors"
        >
          <Plus className="h-4 w-4" strokeWidth={1.75} />
          <span>Nova org.</span>
        </Link>
      </div>
    </div>
  )
}
